'use client'

// imports
import { useEffect, useState } from 'react';

// lib files
import { handleRecommendedShowClick } from '@/lib/content-services/rec-engine';
import safeFetch from '@/lib/helpers/safe-fetch';
import { sendPbsGtmEvent } from '@/lib/helpers/send-pbs-gtm-event';
import { useIdentity } from '@/lib/hooks/useIdentity';

// types
import { ProfileData } from '@/lib/types/api/profile-data';
import type { ShowBadgeFlags } from '@/components/ShowPoster/ShowPoster';
import { ShowRowContent } from "@/lib/types/api/show-data";

// constants
import { RECOMMENDED_SHOWS_FEATURE_CATEGORY } from '@/lib/constants';

// components
import ShowRow from "@/components/ShowRow/ShowRow";


interface RecommendedMysteriesRowProps {
  profileStub?: ProfileData;
  showBadgeFlags?: ShowBadgeFlags;
}

export const RecommendedMysteriesRow = ({
  showBadgeFlags,
}: RecommendedMysteriesRowProps) => {
  const { isAuthenticated, cachedIdentity } = useIdentity();
  const [recommendedMysteries, setRecommendedMysteries] = useState<ShowRowContent[] | null>(null);

  const userFirstName = cachedIdentity?.firstName;

  useEffect(() => {
    if (recommendedMysteries) return;

    const fetchRecommended = async () => {
      try {
        const response = await safeFetch('/api/profile/recommended-mysteries/');
        const json = await response.json();
        const recommendedMysteriesData = json.recommendedShowsData;
        setRecommendedMysteries(recommendedMysteriesData);
      } catch (error) {
        console.error(error);
      }
    };

    if (isAuthenticated) {
      fetchRecommended();
    }
  }, [isAuthenticated, recommendedMysteries]);

  const handleRecommendedShowPosterClick = (show: ShowRowContent, index?: number, linkHref?: string) => {
    const { title, slug } = show;
    handleRecommendedShowClick(show);
    sendPbsGtmEvent({
      show_title: title,
      pbs_content_id: slug,
      object_name: "show poster",
      object_type: "link",
      object_position: index !== undefined ? index + 1 : undefined,
      object_location: `mystery explore hub recommended show row`,
      object_action: "click",
      object_action_behavior: "user navigates to show page",
      object_url: linkHref,
      feature_name: "recommended show poster",
      feature_category: RECOMMENDED_SHOWS_FEATURE_CATEGORY,
      signed_in_status: true,
    });
  }

  // if the user is logged in and the recommendedMysteries are available, show them
  if (isAuthenticated && recommendedMysteries && recommendedMysteries.length > 0) {
    return (
      <ShowRow
        shows={recommendedMysteries}
        onShowPosterClick={handleRecommendedShowPosterClick}
        title={`Top Picks for ${userFirstName ? userFirstName : "You"}`}
        showBadgeFlags={showBadgeFlags}
      />);
  }

  return null;
};

export default RecommendedMysteriesRow;
